'use client'

import { useErrorHandler, useTranslate } from '../../hooks'
import { useRouter } from '../../i18n/navigation'
import { useGetOtcPrice, useOtcPlaceOrder } from '@repo/services'
import { Col } from '@repo/ui/components'
import { useIsDesktop } from '@repo/ui/hooks'
import { useState } from 'react'
import { AdvancedTradeBoxBalanceDisplay } from './advanced-trade-box.balance-display'
import { AdvancedTradeBoxForm } from './advanced-trade-box.form'
import { AdvancedTradeBoxOrderTypeSelect } from './advanced-trade-box.order-type-select'
import { AdvancedTradeBoxTabs } from './advanced-trade-box.tabs'
import type {
  AdvancedTradeBoxFormValues,
  AdvancedTradeBoxOrderType,
  AdvancedTradeBoxProps,
  AdvancedTradeBoxTab,
} from './advanced-trade-box.types'

export const AdvancedTradeBox = ({
  tab: initialTab = 'buy',
  orderType: initialOrderType = 'limit',
  availableAmount = 0,
  availableBalance = 0,
  onSubmit,
  instantBuyPrice = 0,
  instantSellPrice = 0,
  inputCurrency = 'IRR',
  outputCurrency = 'TMN',
  selectedAsset,
}: AdvancedTradeBoxProps) => {
  const { push } = useRouter()
  const [tab, setTab] = useState<AdvancedTradeBoxTab>(initialTab)
  const [orderType, setOrderType] =
    useState<AdvancedTradeBoxOrderType>(initialOrderType)

  const { t } = useTranslate()
  const { errorHandler } = useErrorHandler()
  const isDesktop = useIsDesktop()

  const { data: otcPrice } = useGetOtcPrice({
    symbol: selectedAsset?.token || '',
    base: inputCurrency,
    orderType: tab === 'buy' ? 'buy' : 'sell',
  })

  const currentInstantBuyPrice =
    instantBuyPrice || +(otcPrice?.data?.price ?? 0)
  const currentInstantSellPrice =
    instantSellPrice || +(otcPrice?.data?.price ?? 0)

  const currentAvailableAmount =
    availableAmount || +(otcPrice?.data?.targetMaxAmount ?? 0)
  const currentAvailableBalance =
    availableBalance || +(otcPrice?.data?.baseMaxAmount ?? 0)

  // Helper to get price per token
  const getPrice = (form: AdvancedTradeBoxFormValues) => {
    if (orderType === 'market') {
      return tab === 'buy' ? currentInstantBuyPrice : currentInstantSellPrice
    }
    return form?.price || 0
  }

  // Helper to get total
  const getTotal = (form: AdvancedTradeBoxFormValues) => {
    const price = getPrice(form)
    return Number(((form?.amount || 0) * price).toFixed(0))
  }

  const { mutate: otcPlaceOrder, isPending: isOtcPlaceOrderLoading } =
    useOtcPlaceOrder({
      invalidate: true,
    })

  // Handler for form submit
  const handleFormSubmit = (form: AdvancedTradeBoxFormValues) => {
    otcPlaceOrder(
      {
        submit: true,
        targetCurrency: selectedAsset?.token || '',
        baseCurrency: inputCurrency,
        amount: tab === 'buy' ? getTotal(form) : form.amount,
        orderType: tab === 'buy' ? 'buy' : 'sell',
      },
      {
        onSuccess: () => {
          onSubmit?.(form)
          push('/wallet')
        },
        onError: errorHandler,
      },
    )
  }

  return (
    <div className='!max-w-[688px] w-full mx-auto'>
      <Col className='gap-lg'>
        {/* Desktop: Order Type Select first, then Buy/Sell Tabs */}
        {isDesktop ? (
          <Col className='gap-md'>
            <AdvancedTradeBoxOrderTypeSelect
              orderType={orderType}
              onChange={setOrderType}
            />

            <AdvancedTradeBoxTabs tab={tab} onTabChange={setTab} />

            <AdvancedTradeBoxBalanceDisplay
              title='order_available_balance'
              max={
                tab === 'buy'
                  ? inputCurrency === 'IRR' && outputCurrency === 'TMN'
                    ? currentAvailableBalance / 10
                    : currentAvailableBalance
                  : currentAvailableAmount
              }
              currencyLabel={tab === 'buy' ? t('currency_toman') : t('CM2')}
              onMaxClick={() => push('/wallet/deposit/online-payment')}
              color='neutral'
            />
          </Col>
        ) : (
          /* Mobile: Buy/Sell Tabs first, then Order Type Select */
          <Col className='gap-md'>
            <AdvancedTradeBoxTabs tab={tab} onTabChange={setTab} />

            <AdvancedTradeBoxOrderTypeSelect
              orderType={orderType}
              onChange={setOrderType}
            />
          </Col>
        )}

        <AdvancedTradeBoxForm
          tab={tab}
          orderType={orderType}
          availableAmount={currentAvailableAmount}
          availableBalance={currentAvailableBalance}
          onSubmit={handleFormSubmit}
          isLoading={isOtcPlaceOrderLoading}
          inputCurrency={inputCurrency}
          outputCurrency={outputCurrency}
          instantPrice={
            tab === 'buy' ? currentInstantBuyPrice : currentInstantSellPrice
          }
        />
      </Col>
    </div>
  )
}
